import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2 } from "lucide-react";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Toaster sonner integrado ao Design System TEG.
 *
 * Cada tipo (success/error/warning/info) ganha cor + ícone semânticos
 * via tokens (`--success`, `--destructive`, `--warning`, `--info`),
 * eliminando o problema de feedback monocromático onde erro e sucesso
 * eram visualmente idênticos.
 *
 * `richColors` é desligado — fazemos override manual via classNames para
 * usar os tokens HSL do projeto em vez da paleta padrão do sonner.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <div aria-live="polite" aria-atomic="true">
      <Sonner
        theme={theme as ToasterProps["theme"]}
        className="toaster group"
        icons={{
          success: <CheckCircle2 className="h-5 w-5 shrink-0" />,
          error: <XCircle className="h-5 w-5 shrink-0" />,
          warning: <AlertTriangle className="h-5 w-5 shrink-0" />,
          info: <Info className="h-5 w-5 shrink-0" />,
          loading: <Loader2 className="h-5 w-5 shrink-0 animate-spin" />,
        }}
        toastOptions={{
          classNames: {
            toast:
              "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
            title: "group-[.toast]:font-medium",
            description: "group-[.toast]:text-muted-foreground",
            actionButton:
              "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
            cancelButton:
              "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
            // Tipos semânticos — cor sólida + foreground legível em ambos os temas
            success:
              "group-[.toaster]:bg-success! group-[.toaster]:text-success-foreground! group-[.toaster]:border-success/40! **:data-description:text-success-foreground/90! **:data-icon:text-success-foreground!",
            error:
              "group-[.toaster]:bg-destructive! group-[.toaster]:text-destructive-foreground! group-[.toaster]:border-destructive/40! **:data-description:text-destructive-foreground/90! **:data-icon:text-destructive-foreground!",
            warning:
              "group-[.toaster]:bg-warning! group-[.toaster]:text-warning-foreground! group-[.toaster]:border-warning/40! **:data-description:text-warning-foreground/90! **:data-icon:text-warning-foreground!",
            info:
              "group-[.toaster]:bg-info! group-[.toaster]:text-info-foreground! group-[.toaster]:border-info/40! **:data-description:text-info-foreground/90! **:data-icon:text-info-foreground!",
          },
        }}
        {...props}
      />
    </div>
  );
};

export { Toaster, toast };
